import React, { useState, useEffect } from 'react';
import { 
  Search, Plus, Receipt, MapPin, Users, Calendar, CreditCard, 
  CheckCircle, Clock, Wifi, X, Smartphone, Bell, Settings,
  Navigation, Home, User, LogOut, Menu, Camera, Phone,
  Star, AlertCircle, Truck, Battery, Signal, Eye, 
  Filter, Download, Share, MoreVertical, Target,
  Zap, DollarSign, TrendingUp, Award
} from 'lucide-react';

// Type Definitions
interface Property {
  id: number;
  roadNo: string;
  plotNo: string;
  name: string;
  category: 'Residential' | 'Commercial';
  monthlyRate: number;
  paymentFrequency: 'monthly' | 'quarterly' | 'yearly';
  dues: number;
  penalty: number;
  phone: string;
  address: string;
  lastPayment: string;
  nextDue: string;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  gps: { lat: number; lng: number };
  visited: boolean;
}

interface Transaction {
  id: number;
  propertyId: number;
  propertyName: string;
  plotNo: string;
  roadNo: string;
  amount: number;
  paymentMethod: 'cash' | 'card' | 'upi' | 'bank';
  paymentFrequency: 'monthly' | 'quarterly' | 'yearly';
  totalDue: number;
  remainingDue: number;
  date: string;
  receiptNo: string;
  agentId: string;
  agentName: string;
}

interface Agent {
  id: string;
  name: string;
  phone: string;
  area: string;
  avatar: string;
  rating: number;
  collections: number;
  target: number;
}

interface PaymentFrequency {
  id: 'monthly' | 'quarterly' | 'yearly';
  name: string;
  multiplier: number;
  icon: string;
  discount?: number;
}

interface PaymentMethod {
  id: 'cash' | 'card' | 'upi' | 'bank';
  name: string;
  icon: string;
}

interface Notification {
  id: number;
  title: string;
  message: string;
  time: string;
  type: 'info' | 'success' | 'warning';
}

const WasteAgentApp = () => {
  // State Management
  const [properties, setProperties] = useState<Property[]>([
    { 
      id: 1, roadNo: '68', plotNo: '1345/1', name: 'Srinivasa Raja', category: 'Residential', 
      monthlyRate: 200, paymentFrequency: 'monthly', dues: 400, penalty: 50,
      phone: '9876543210', address: 'Plot 1345/1, Road 68, Jubilee Hills',
      lastPayment: '2024-01-15', nextDue: '2024-04-15', priority: 'high',
      gps: { lat: 17.4239, lng: 78.4738 }, visited: false
    },
    { 
      id: 2, roadNo: '68', plotNo: '1354', name: 'PETROL BUNK', category: 'Commercial', 
      monthlyRate: 500, paymentFrequency: 'quarterly', dues: 1500, penalty: 150,
      phone: '9876543211', address: 'Plot 1354, Road 68, Jubilee Hills',
      lastPayment: '2023-12-20', nextDue: '2024-06-20', priority: 'medium',
      gps: { lat: 17.4245, lng: 78.4742 }, visited: true
    },
    { 
      id: 3, roadNo: '68', plotNo: '1299D', name: 'Office Complex', category: 'Commercial', 
      monthlyRate: 300, paymentFrequency: 'yearly', dues: 3600, penalty: 360,
      phone: '9876543212', address: 'Plot 1299D, Road 68, Jubilee Hills',
      lastPayment: '2023-02-01', nextDue: '2024-02-01', priority: 'urgent',
      gps: { lat: 17.4251, lng: 78.4748 }, visited: false
    }
  ]);

  const [agent] = useState<Agent>({
    id: 'AG001',
    name: 'Mohammed Ali',
    phone: '9876543203',
    area: 'Jubilee Hills - Sector 1',
    avatar: '👨',
    rating: 4.8,
    collections: 156,
    target: 200
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'upi' | 'bank'>('cash');
  const [paymentFrequency, setPaymentFrequency] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [currentReceipt, setCurrentReceipt] = useState<Transaction | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  const [showNotifications, setShowNotifications] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  // Simulated location and device status
  const [deviceStatus] = useState({
    battery: 78,
    signal: 'strong',
    gps: true,
    lastSync: new Date().toLocaleTimeString()
  });

  const paymentFrequencies: PaymentFrequency[] = [
    { id: 'monthly', name: 'Monthly', multiplier: 1, icon: '📅' },
    { id: 'quarterly', name: 'Quarterly', multiplier: 3, icon: '📊', discount: 5 },
    { id: 'yearly', name: 'Yearly', multiplier: 12, icon: '🗓️', discount: 10 }
  ];

  const paymentMethods: PaymentMethod[] = [
    { id: 'cash', name: 'Cash', icon: '💵' },
    { id: 'card', name: 'Card', icon: '💳' },
    { id: 'upi', name: 'UPI', icon: '📱' },
    { id: 'bank', name: 'Bank Transfer', icon: '🏦' }
  ];

  const notifications: Notification[] = [
    { id: 1, title: 'Route Updated', message: '3 new properties added to your route', time: '5m ago', type: 'info' },
    { id: 2, title: 'Payment Target', message: '85% of monthly target achieved', time: '1h ago', type: 'success' },
    { id: 3, title: 'High Priority', message: 'Office Complex payment overdue', time: '2h ago', type: 'warning' }
  ];

  // Utility Functions
  const calculateTotal = (property: Property): number => {
    return property.monthlyRate + property.dues + property.penalty;
  };

  const calculateAmountByFrequency = (property: Property, frequency: string): number => {
    const freq = paymentFrequencies.find(f => f.id === frequency);
    if (!freq) return property.monthlyRate;
    
    const baseAmount = property.monthlyRate * freq.multiplier;
    const discount = freq.discount ? (baseAmount * freq.discount / 100) : 0;
    return baseAmount - discount;
  };

  const getStatusColor = (property: Property): string => {
    const total = calculateTotal(property);
    if (total <= property.monthlyRate) return 'bg-green-50 text-green-700 border-green-200';
    if (property.penalty === 0) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  const getStatus = (property: Property): string => {
    const total = calculateTotal(property);
    if (total <= property.monthlyRate) return 'Current';
    if (property.penalty === 0) return 'Due';
    return 'Overdue';
  };

  const getPriorityColor = (priority: Property['priority']): string => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-green-500';
    }
  };

  const filteredProperties = properties
    .filter(property => {
      const matchesSearch = property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           property.plotNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           property.phone.includes(searchTerm);
      
      if (filterStatus === 'all') return matchesSearch;
      if (filterStatus === 'visited') return matchesSearch && property.visited;
      if (filterStatus === 'pending') return matchesSearch && !property.visited;
      if (filterStatus === 'overdue') return matchesSearch && property.penalty > 0;
      
      return matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'priority') {
        const priorityOrder: Record<Property['priority'], number> = { urgent: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      if (sortBy === 'amount') {
        return calculateTotal(b) - calculateTotal(a);
      }
      return a.name.localeCompare(b.name);
    });

  const todayStats = {
    collected: transactions.reduce((sum, t) => sum + t.amount, 0),
    visited: properties.filter(p => p.visited).length,
    target: 15000,
    properties: properties.length
  };

  const handlePayment = () => {
    if (!selectedProperty || !paymentAmount) return;

    const amount = parseFloat(paymentAmount);
    const total = calculateTotal(selectedProperty);
    
    const newReceipt: Transaction = {
      id: Date.now(),
      propertyId: selectedProperty.id,
      propertyName: selectedProperty.name,
      plotNo: selectedProperty.plotNo,
      roadNo: selectedProperty.roadNo,
      amount: amount,
      paymentMethod: paymentMethod,
      paymentFrequency: paymentFrequency,
      totalDue: total,
      remainingDue: Math.max(0, total - amount),
      date: new Date().toLocaleString(),
      receiptNo: `AG${Date.now().toString().slice(-6)}`,
      agentId: agent.id,
      agentName: agent.name
    };

    const updatedProperties = properties.map(prop => {
      if (prop.id === selectedProperty.id) {
        const remaining = Math.max(0, total - amount);
        return {
          ...prop,
          dues: Math.max(0, remaining - prop.monthlyRate),
          penalty: remaining > prop.monthlyRate ? Math.floor((remaining - prop.monthlyRate) * 0.1) : 0,
          paymentFrequency: paymentFrequency,
          lastPayment: new Date().toISOString().split('T')[0],
          visited: true
        };
      }
      return prop;
    });

    setProperties(updatedProperties);
    setCurrentReceipt(newReceipt);
    setTransactions([newReceipt, ...transactions]);
    setPaymentAmount('');
    setShowPaymentModal(false);
    setShowReceiptModal(true);
  };

  const quickPaymentOptions = (property: Property) => {
    return paymentFrequencies.map(freq => ({
      ...freq,
      amount: calculateAmountByFrequency(property, freq.id)
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <span className="text-lg">{agent.avatar}</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Agent App</h1>
                <p className="text-xs text-gray-500">{agent.area}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowNotifications(true)}
                className="relative p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button>
              <button
                onClick={() => setShowProfileModal(true)}
                className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                <User className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Device Status Bar */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-4 text-xs">
              <div className="flex items-center space-x-1">
                <Battery className={`w-4 h-4 ${deviceStatus.battery > 20 ? 'text-green-500' : 'text-red-500'}`} />
                <span className="text-gray-600">{deviceStatus.battery}%</span>
              </div>
              <div className="flex items-center space-x-1">
                <Signal className="w-4 h-4 text-green-500" />
                <span className="text-gray-600">{deviceStatus.signal}</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                <span className="text-gray-600">{isOnline ? 'Online' : 'Offline'}</span>
              </div>
            </div>
            <span className="text-xs text-gray-500">Sync: {deviceStatus.lastSync}</span>
          </div>
        </div>
      </header>

      <div className="px-4 py-4 pb-20">
        {/* Home Tab */}
        {activeTab === 'home' && (
          <div className="space-y-4">
            {/* Daily Stats Dashboard */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Today's Progress</h2>
                <span className="text-xs text-gray-500">{new Date().toLocaleDateString()}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <DollarSign className="w-6 h-6 text-green-600" />
                    <span className="text-lg font-bold text-green-600">₹{todayStats.collected.toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-green-700 font-medium mt-1">Collected</div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <Target className="w-6 h-6 text-blue-600" />
                    <span className="text-lg font-bold text-blue-600">{todayStats.visited}/{todayStats.properties}</span>
                  </div>
                  <div className="text-xs text-blue-700 font-medium mt-1">Visited</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Target Progress</span>
                  <span className="text-sm font-medium text-gray-900">
                    {Math.round((todayStats.collected / todayStats.target) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((todayStats.collected / todayStats.target) * 100, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Target: ₹{todayStats.target.toLocaleString()}
                </div>
              </div>

              {/* Agent Rating */}
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-xl">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  <span className="text-sm font-medium text-gray-900">Agent Rating</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-lg font-bold text-yellow-600">{agent.rating}</span>
                  <span className="text-xs text-gray-500">/5.0</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setActiveTab('properties')}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">Properties</span>
                  <span className="text-xs text-gray-500">{properties.length} total</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('route')}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                    <Navigation className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">Route</span>
                  <span className="text-xs text-gray-500">Optimized</span>
                </div>
              </button>
            </div>

            {/* Priority Properties */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-md font-bold text-gray-900">Priority Properties</h3>
                <button
                  onClick={() => setActiveTab('properties')}
                  className="text-xs text-blue-600 font-medium"
                >
                  View All
                </button>
              </div>
              
              <div className="space-y-3">
                {properties
                  .filter(p => p.priority === 'urgent' || p.priority === 'high')
                  .slice(0, 3)
                  .map(property => (
                    <div key={property.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${getPriorityColor(property.priority)}`}></div>
                        <div>
                          <div className="font-medium text-sm text-gray-900">{property.name}</div>
                          <div className="text-xs text-gray-500">Plot {property.plotNo}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-red-600">₹{calculateTotal(property)}</div>
                        <div className="text-xs text-gray-500 capitalize">{property.priority}</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div className="space-y-4">
            {/* Search and Filters */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search properties..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-3 w-full border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm text-sm"
                />
              </div>

              <div className="flex space-x-2 overflow-x-auto pb-2">
                {[
                  { id: 'all', name: 'All', count: properties.length },
                  { id: 'pending', name: 'Pending', count: properties.filter(p => !p.visited).length },
                  { id: 'visited', name: 'Visited', count: properties.filter(p => p.visited).length },
                  { id: 'overdue', name: 'Overdue', count: properties.filter(p => p.penalty > 0).length }
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setFilterStatus(filter.id)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                      filterStatus === filter.id
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-white text-gray-600 border border-gray-200'
                    }`}
                  >
                    {filter.name} ({filter.count})
                  </button>
                ))}
              </div>

              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white"
                >
                  <option value="priority">Sort by Priority</option>
                  <option value="amount">Sort by Amount</option>
                  <option value="name">Sort by Name</option>
                </select>
              </div>
            </div>

            {/* Properties List */}
            <div className="space-y-3">
              {filteredProperties.map(property => (
                <div key={property.id} className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start space-x-3">
                      <div className={`w-3 h-3 rounded-full mt-2 ${getPriorityColor(property.priority)}`}></div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-sm font-bold text-gray-900">{property.name}</h3>
                          {property.visited && <CheckCircle className="w-4 h-4 text-green-500" />}
                        </div>
                        <p className="text-xs text-gray-500">Plot {property.plotNo} • Road {property.roadNo}</p>
                        <p className="text-xs text-gray-400 mt-1">{property.category}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`px-2 py-1 rounded-lg text-xs font-semibold border ${getStatusColor(property)}`}>
                        {getStatus(property)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center p-2 bg-blue-50 rounded-lg">
                      <div className="text-xs text-gray-500">Base</div>
                      <div className="text-sm font-bold text-blue-600">₹{property.monthlyRate}</div>
                    </div>
                    <div className="text-center p-2 bg-yellow-50 rounded-lg">
                      <div className="text-xs text-gray-500">Dues</div>
                      <div className="text-sm font-bold text-yellow-600">₹{property.dues}</div>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded-lg">
                      <div className="text-xs text-gray-500">Total</div>
                      <div className="text-sm font-bold text-red-600">₹{calculateTotal(property)}</div>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedProperty(property);
                        setShowPaymentModal(true);
                      }}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-2 rounded-xl font-medium text-sm flex items-center justify-center space-x-2 hover:from-green-600 hover:to-green-700 transition-all shadow-md"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>Collect</span>
                    </button>
                    
                    <button
                      onClick={() => window.open(`tel:${property.phone}`)}
                      className="px-3 py-2 bg-blue-100 text-blue-600 rounded-xl hover:bg-blue-200 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => window.open(`https://maps.google.com/?q=${property.gps.lat},${property.gps.lng}`)}
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      <Navigation className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Route Tab */}
        {activeTab === 'route' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Optimized Route</h2>
                <button className="text-blue-600 text-sm font-medium">Re-optimize</button>
              </div>
              
              <div className="space-y-3">
                {properties
                  .sort((a, b) => {
                    const priorityOrder: Record<Property['priority'], number> = { urgent: 0, high: 1, medium: 2, low: 3 };
                    return priorityOrder[a.priority] - priorityOrder[b.priority];
                  })
                  .map((property, index) => (
                    <div key={property.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-sm">{property.name}</span>
                          {property.visited && <CheckCircle className="w-4 h-4 text-green-500" />}
                        </div>
                        <div className="text-xs text-gray-500">₹{calculateTotal(property)} • {property.address}</div>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${getPriorityColor(property.priority)}`}></div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Collections Tab */}
        {activeTab === 'collections' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Today's Collections</h2>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-600">₹{todayStats.collected.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">{transactions.length} transactions</div>
                </div>
              </div>

              {transactions.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No collections yet today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map(transaction => (
                    <div key={transaction.id} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-medium text-sm text-gray-900">{transaction.propertyName}</h4>
                          <p className="text-xs text-gray-500">#{transaction.receiptNo}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">₹{transaction.amount}</div>
                          <div className="text-xs text-gray-500 capitalize">{transaction.paymentMethod}</div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-400">{transaction.date}</span>
                        <button
                          onClick={() => {
                            setCurrentReceipt(transaction);
                            setShowReceiptModal(true);
                          }}
                          className="text-blue-600 text-xs font-medium"
                        >
                          View Receipt
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedProperty && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-3xl p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Collect Payment</h2>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 mb-4">
              <h3 className="text-lg font-bold text-gray-900 mb-1">{selectedProperty.name}</h3>
              <p className="text-sm text-gray-600 mb-1">Plot {selectedProperty.plotNo} • Road {selectedProperty.roadNo}</p>
              <p className="text-xs text-gray-500">{selectedProperty.phone}</p>
            </div>

            <div className="mb-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Payment Options</h4>
              <div className="space-y-2">
                {quickPaymentOptions(selectedProperty).map(option => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setPaymentFrequency(option.id);
                      setPaymentAmount(option.amount.toString());
                    }}
                    className={`w-full p-3 rounded-xl border-2 transition-all ${
                      paymentFrequency === option.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-xl">{option.icon}</span>
                        <div className="text-left">
                          <div className="font-semibold text-sm text-gray-900">{option.name}</div>
                          <div className="text-xs text-gray-500">
                            {option.multiplier} month{option.multiplier > 1 ? 's' : ''}
                            {option.discount && ` • ${option.discount}% off`}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">₹{option.amount}</div>
                        {option.discount && (
                          <div className="text-xs text-green-500">
                            Save ₹{(selectedProperty.monthlyRate * option.multiplier * option.discount / 100)}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-red-50 rounded-2xl p-4 mb-4 border border-red-200">
              <h4 className="font-semibold text-red-900 mb-3">Outstanding Amount</h4>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center">
                  <div className="text-xs text-gray-500">Base</div>
                  <div className="font-bold text-gray-900">₹{selectedProperty.monthlyRate}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Dues</div>
                  <div className="font-bold text-yellow-600">₹{selectedProperty.dues}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500">Penalty</div>
                  <div className="font-bold text-red-600">₹{selectedProperty.penalty}</div>
                </div>
              </div>
              <div className="bg-white p-3 rounded-xl border border-red-200">
                <div className="text-center">
                  <div className="text-sm text-gray-600">Total Outstanding</div>
                  <div className="text-xl font-bold text-red-600">₹{calculateTotal(selectedProperty)}</div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-md font-semibold text-gray-900 mb-2">Payment Amount</label>
              <input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full p-3 border-2 border-gray-200 rounded-xl text-xl font-bold text-center focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
              />
              <button
                onClick={() => setPaymentAmount(calculateTotal(selectedProperty).toString())}
                className="w-full mt-2 py-2 text-sm text-green-600 border-2 border-green-200 rounded-xl hover:bg-green-50 font-medium transition-colors"
              >
                Pay Full Outstanding (₹{calculateTotal(selectedProperty)})
              </button>
            </div>

            <div className="mb-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Payment Method</h4>
              <div className="grid grid-cols-2 gap-2">
                {paymentMethods.map(method => (
                  <button
                    key={method.id}
                    onClick={() => setPaymentMethod(method.id)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      paymentMethod === method.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-xl mb-1">{method.icon}</div>
                      <div className="font-medium text-sm text-gray-900">{method.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="py-3 px-4 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                disabled={!paymentAmount}
                className="py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed hover:from-green-600 hover:to-green-700 transition-all shadow-lg"
              >
                Process Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && currentReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-green-600 mb-2">Payment Successful!</h2>
              <p className="text-gray-500 text-sm">Digital receipt generated</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 mb-6 space-y-3">
              <div className="text-center pb-3 border-b border-gray-200">
                <div className="text-xs text-gray-500">Receipt #</div>
                <div className="font-bold">{currentReceipt.receiptNo}</div>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Property:</span>
                <span className="font-semibold">{currentReceipt.propertyName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Plot No:</span>
                <span className="font-semibold">{currentReceipt.plotNo}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Agent:</span>
                <span className="font-semibold">{currentReceipt.agentName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Method:</span>
                <span className="font-semibold capitalize">{currentReceipt.paymentMethod}</span>
              </div>
              
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-bold text-green-600 text-lg">₹{currentReceipt.amount}</span>
                </div>
                {currentReceipt.remainingDue > 0 && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Remaining Due:</span>
                    <span className="font-semibold text-red-600">₹{currentReceipt.remainingDue}</span>
                  </div>
                )}
              </div>
              
              <div className="text-center pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500">Payment Date</div>
                <div className="font-semibold text-sm">{currentReceipt.date}</div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: 'Payment Receipt',
                      text: `Payment receipt for ${currentReceipt.propertyName} - ₹${currentReceipt.amount}`,
                      url: window.location.href
                    });
                  } else {
                    alert('Receipt details copied!');
                  }
                }}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:bg-blue-700 transition-colors"
              >
                <Share className="w-4 h-4" />
                <span>Share Receipt</span>
              </button>
              
              <button
                onClick={() => setShowReceiptModal(false)}
                className="w-full bg-gray-600 text-white py-3 rounded-xl font-semibold hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Modal */}
      {showNotifications && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-3xl p-4 max-h-[70vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
              <button
                onClick={() => setShowNotifications(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="space-y-3">
              {notifications.map(notification => (
                <div key={notification.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      notification.type === 'success' ? 'bg-green-500' :
                      notification.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}></div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm text-gray-900">{notification.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                      <span className="text-xs text-gray-400 mt-2 block">{notification.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg text-3xl">
                {agent.avatar}
              </div>
              <h2 className="text-xl font-bold text-gray-900">{agent.name}</h2>
              <p className="text-gray-500 text-sm">Agent ID: {agent.id}</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-600">Phone:</span>
                <span className="font-semibold">{agent.phone}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-600">Area:</span>
                <span className="font-semibold">{agent.area}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-600">Collections:</span>
                <span className="font-semibold">{agent.collections} this month</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-xl">
                <span className="text-gray-600">Rating:</span>
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="font-semibold">{agent.rating}/5.0</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:bg-blue-700 transition-colors">
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </button>
              
              <button
                onClick={() => setShowProfileModal(false)}
                className="w-full bg-gray-600 text-white py-3 rounded-xl font-semibold hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-40">
        <div className="flex justify-around">
          {[
            { id: 'home', name: 'Home', icon: Home },
            { id: 'properties', name: 'Properties', icon: MapPin },
            { id: 'route', name: 'Route', icon: Navigation },
            { id: 'collections', name: 'Collections', icon: Receipt }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center py-2 px-3 rounded-xl transition-all duration-200 ${
                  activeTab === tab.id 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-5 h-5 mb-1" />
                <span className="text-xs font-medium">{tab.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WasteAgentApp;